import SelectInput from "../shared/Inputs/SelectInput";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";

interface AccountEditAddressesSectionProps {
  register: any;
  setValue: any;
  watch: any;
  addressOptions: { id: string; name: string }[];
  selectedBillingAddressName?: string;
  selectedShippingAddressName?: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  canAddAddress: boolean;
  onAddAddress: () => void;
  onEditBillingAddress: () => void;
  onEditShippingAddress: () => void;
  addressesLoading: boolean;
  addAddressHelperText?: string;
}

export function AccountEditAddressesSection({
  register,
  setValue,
  watch,
  addressOptions,
  selectedBillingAddressName,
  selectedShippingAddressName,
  isOpen,
  setIsOpen,
  canAddAddress,
  onAddAddress,
  onEditBillingAddress,
  onEditShippingAddress,
  addressesLoading,
  addAddressHelperText,
}: AccountEditAddressesSectionProps) {
  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="pb-1 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-800">Addresses</p>
          <p className="text-xs text-gray-500">Defaults for billing and delivery</p>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex h-8 w-8 items-center justify-center text-gray-500 hover:text-gray-800 transition-colors"
          aria-label={isOpen ? "Collapse addresses section" : "Expand addresses section"}
        >
          {isOpen ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <SelectInput
                label="Default Billing Address"
                register={register}
                registerName="billingAddressId"
                setValue={setValue}
                watch={watch}
                options={addressOptions}
                disabled={addressesLoading || addressOptions.length === 0}
                rounded
              />
            </div>
            <button
              type="button"
              onClick={onEditBillingAddress}
              className="mt-1 inline-flex h-[38px] w-10 items-center justify-center rounded-md border border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!watch("billingAddressId")}
              aria-label="Edit billing address"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <SelectInput
                label="Default Delivery Address"
                register={register}
                registerName="shippingAddressId"
                setValue={setValue}
                watch={watch}
                options={addressOptions}
                disabled={addressesLoading || addressOptions.length === 0}
                rounded
              />
            </div>
            <button
              type="button"
              onClick={onEditShippingAddress}
              className="mt-1 inline-flex h-[38px] w-10 items-center justify-center rounded-md border border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!watch("shippingAddressId")}
              aria-label="Edit delivery address"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>

          <button
            type="button"
            onClick={onAddAddress}
            className="inline-flex h-10 w-full items-center justify-center rounded-md border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canAddAddress}
          >
            <PlusCircleIcon className="mr-2 h-5 w-5 text-gray-600" />
            Add Address
          </button>

          {addAddressHelperText && (
            <p className="text-xs italic text-gray-500">
              {addAddressHelperText}
            </p>
          )}

          {addressOptions.length === 0 && !addAddressHelperText && (
            <p className="text-xs italic text-gray-500">
              Add account addresses to enable billing and delivery defaults.
            </p>
          )}

          {(selectedBillingAddressName || selectedShippingAddressName) && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-sm">
              {selectedBillingAddressName && (
                <span className="inline-flex items-center rounded-full bg-white border border-gray-200 px-2.5 py-1 text-xs text-gray-700 mr-2">
                  Billing: <span className="font-medium ml-1">{selectedBillingAddressName}</span>
                </span>
              )}
              {selectedShippingAddressName && (
                <span className="inline-flex items-center rounded-full bg-white border border-gray-200 px-2.5 py-1 text-xs text-gray-700">
                  Delivery: <span className="font-medium ml-1">{selectedShippingAddressName}</span>
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

